'use client';

import { BillingCycle, Currency } from '@prezly/sdk';
import classNames from 'classnames';
import { Check } from 'lucide-react';

import { usePricingPageContext } from '@/pricing/PricingPageContext';
import * as Pricing from '@/pricing/types';
import { ButtonLink } from '@/src/ui/Button/ButtonLink';
import { Button as DropdownItem } from '@/src/ui/Dropdown/List/components/Item/Button';
import { ItemsGroup } from '@/src/ui/Dropdown/List/components/ItemsGroup';
import { DropdownButton } from '@/ui/DropdownButton/DropdownButton';

import styles from './PricingToggle.module.scss';

export function PricingToggle() {
    const { billingCycle, currency, pricingPlanType, toggleBillingCycle, toggleCurrency } =
        usePricingPageContext();

    const isStandardPricing = pricingPlanType === Pricing.TableId.STANDARD;
    const isAgencyPricing = pricingPlanType === Pricing.TableId.AGENCY;

    const isMonthlyCycle = billingCycle === BillingCycle.MONTH;
    const isUsd = currency === Currency.USD;

    return (
        <div className={styles.wrapper}>
            <DropdownButton
                className={styles.dropdownButton}
                labelClassName={styles.label}
                label={isMonthlyCycle ? 'Monthly' : 'Yearly'}
            >
                <ItemsGroup className={styles.dropdown}>
                    <DropdownItem
                        className={classNames(styles.item, {
                            [styles.active]: isMonthlyCycle,
                        })}
                        onClick={toggleBillingCycle}
                        disabled={isMonthlyCycle}
                        iconRight={isMonthlyCycle ? Check : undefined}
                    >
                        Monthly
                    </DropdownItem>
                    <DropdownItem
                        className={classNames(styles.item, {
                            [styles.active]: !isMonthlyCycle,
                        })}
                        onClick={toggleBillingCycle}
                        disabled={!isMonthlyCycle}
                        iconRight={!isMonthlyCycle ? Check : undefined}
                    >
                        Yearly
                    </DropdownItem>
                </ItemsGroup>
            </DropdownButton>

            <div className={styles.planTypeToggle}>
                <ButtonLink
                    active={isStandardPricing}
                    className={styles.button}
                    href="/pricing"
                    size="large"
                    variant={isStandardPricing ? 'dark' : 'secondary'}
                >
                    Brands
                </ButtonLink>
                <ButtonLink
                    active={isAgencyPricing}
                    className={styles.button}
                    href="/pricing/agency"
                    size="large"
                    variant={isAgencyPricing ? 'dark' : 'secondary'}
                >
                    Agencies
                </ButtonLink>
            </div>

            <DropdownButton
                className={classNames(styles.dropdownButton, styles.alignToRight)}
                labelClassName={styles.label}
                label={isUsd ? 'US Dollars ($)' : 'Euro (€)'}
                menuPlacement="bottom-end"
            >
                <ItemsGroup className={styles.dropdown}>
                    <DropdownItem
                        className={classNames(styles.item, {
                            [styles.active]: !isUsd,
                        })}
                        onClick={toggleCurrency}
                        disabled={!isUsd}
                        iconRight={!isUsd ? Check : undefined}
                    >
                        Euro (€)
                    </DropdownItem>
                    <DropdownItem
                        className={classNames(styles.item, {
                            [styles.active]: isUsd,
                        })}
                        onClick={toggleCurrency}
                        disabled={isUsd}
                        iconRight={isUsd ? Check : undefined}
                    >
                        US Dollars ($)
                    </DropdownItem>
                </ItemsGroup>
            </DropdownButton>
        </div>
    );
}
